import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DashboardService } from '@/modules/dashboard'
import { useAuth } from '@/modules/auth'
import { useTripStore } from '@/stores'
import { DashboardData } from '@/shared/api'

export function useDashboard() {
  const { getAccessToken, isAuthenticated } = useAuth()
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const hasLoadedRef = useRef(false)

  // Create dashboard service instance
  const dashboardService = useMemo(() => new DashboardService(getAccessToken), [getAccessToken])
  
  // Get trips from store to listen for changes
  const trips = useTripStore(state => state.trips || [])

  // Load dashboard data
  const loadDashboard = useCallback(async () => {
    if (!isAuthenticated) return

    setIsLoading(true)
    setError(null)
    
    try {
      const data = await dashboardService.getDashboard()
      setDashboardData(data)
      hasLoadedRef.current = true
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load dashboard data'
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }, [dashboardService, isAuthenticated])

  // Load dashboard data on mount (only once)
  useEffect(() => {
    if (isAuthenticated && !hasLoadedRef.current) {
      loadDashboard()
    }
  }, [isAuthenticated]) // Removed loadDashboard from dependencies to prevent infinite loop

  // Refresh dashboard when trips change (but only if we have trips and have already loaded)
  useEffect(() => {
    if (trips.length > 0 && hasLoadedRef.current) {
      loadDashboard()
    }
  }, [trips.length]) // Removed loadDashboard from dependencies to prevent infinite loop

  // Refresh dashboard data
  const refreshDashboard = useCallback(() => {
    if (isAuthenticated) {
      loadDashboard()
    }
  }, [isAuthenticated]) // Removed loadDashboard from dependencies to prevent infinite loop

  // Clear error
  const clearError = useCallback(() => {
    setError(null)
  }, [])

  return {
    // State
    dashboardData,
    isLoading,
    error,
    
    // Actions
    loadDashboard,
    refreshDashboard,
    clearError,
    
    // Computed values
    stats: dashboardData?.stats || null,
    recentTrips: dashboardData?.recentTrips || [],
    upcomingTrips: dashboardData?.upcomingTrips || []
  }
}

